import { FLAGS } from '@iheartjane/feature-flags';
import { getApplicableStoreSpecialIds } from '@iheartjane/menu';
import { registerJdid as registerJdidToRum } from '@iheartjane/tracking';
import type { Nullable } from '@iheartjane/types';
import { useEffect, useRef } from 'react';
import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from 'react-router';
import { data, useLoaderData } from 'react-router';

import { getProductReviews } from '@jane/bloom/dataAccess/algolia/productReviews.server';
import { fetchAeropayUser } from '@jane/bloom/dataAccess/marketplace/aeropay.server';
import {
  fetchBrandSpecials,
  fetchBrandSpecialsQualifyingStatuses,
} from '@jane/bloom/dataAccess/marketplace/brandSpecials.server';
import { fetchJaneGoldConfig } from '@jane/bloom/dataAccess/marketplace/janeGoldConfig.server';
import {
  fetchAllStoreSpecials,
  fetchStoreSpecialsQualifyingStatuses,
} from '@jane/bloom/dataAccess/marketplace/specials.server';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { globalSubloader } from '@jane/bloom/loaders/globalSubloader.server';
import { pdpRouteMeta } from '@jane/bloom/resources/meta/routes/pdp';
import { ddTrace } from '@jane/bloom/services/dd-trace.server';
import { registerJdid } from '@jane/bloom/services/tracking/mixpanel';
import { trackViewedProduct } from '@jane/bloom/services/tracking/track';
import { getCartInfo } from '@jane/bloom/sessions/cart.server';
import { getUserIdentity } from '@jane/bloom/sessions/userIdentity.server';
import type { SortOrder } from '@jane/bloom/types/schemas/algolia/review';
import type { RouteHandle } from '@jane/bloom/types/schemas/bloom/routeHandle';
import type { StoreSpecials } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { getDefaultCart } from '@jane/bloom/utils/cart/getDefaultCart.server';
import { initializeDMContext } from '@jane/bloom/utils/dm/dmContext.server';
import { ensureBloomMenu } from '@jane/bloom/utils/ensureBloomMenu.server';
import { getShouldShowGold } from '@jane/bloom/utils/gold/getShouldShowGold';
import { isFeatureFlagsEnabled } from '@jane/bloom/utils/isFeatureFlagEnabled.server';
import { logError } from '@jane/bloom/utils/logError.server';
import { redirectToParentNotFound } from '@jane/bloom/utils/redirectToParentNotFound.server';
import { promiseHashAllSettled } from '@jane/bloom/utils/requests/promiseHashAllSettled';
import { getCustomLanguage } from '@jane/bloom/viewModels/getCustomLanguage.server';
import { fetchMenuProduct } from '@jane/bloom/viewModels/menuProduct.server';
import { buildProductStoreViewModel } from '@jane/bloom/viewModels/productDetail';
import { buildProductReviewsViewModel } from '@jane/bloom/viewModels/productReviews';
import { buildRecommendedPDPRowViewModel } from '@jane/bloom/viewModels/recommendedPDPRow.server';

import { DMProductDetailsRow } from './components/dmPDPRow';
import { ProductDetailCard } from './components/productDetailCard';
import { ProductReviewsSection } from './components/productDetailCard/productReviews/productReviewsSection';
import stylesLink from './styles.css?url';
import desktopStylesLink from './styles.desktop.css?url';
import type { LoaderResponse } from './types';

export const handle: RouteHandle = {
  hydrate: true,
};

export const links: LinksFunction = () => [
  { href: stylesLink, rel: 'stylesheet' },
  { href: desktopStylesLink, media: '(min-width: 767px)', rel: 'stylesheet' },
];

export const meta: MetaFunction<typeof loader> = (args) => {
  return pdpRouteMeta(args);
};

async function _loader({ params, request }: LoaderFunctionArgs) {
  const productId = params['productId'];
  if (!productId) {
    logError(`productId missing: ${productId}`);
    throw redirectToParentNotFound();
  }

  const menu = await ensureBloomMenu(request);
  const { pinnedStore, shoppingPreferences } = await globalSubloader(
    request,
    menu
  );

  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const reviewsPageNumber = searchParams.get('reviewsPage');
  const reviewsSort = searchParams.get('reviewsSort') as Nullable<SortOrder>;

  const cookie = request.headers.get('Cookie');

  // Kick off independent requests immediately (not used until final response)
  const brandSpecialsPromise = fetchBrandSpecials({ storeId: pinnedStore.id });
  const goldConfigPromise = fetchJaneGoldConfig();
  const storeSpecialsPromise = fetchAllStoreSpecials(
    shoppingPreferences.storeId,
    'schedule'
  );
  const productReviewsPromise = getProductReviews({
    pageNumber: reviewsPageNumber,
    productId: Number(productId),
    sort: reviewsSort,
  });

  // Wave 1: Fetch critical dependencies needed for validation and Wave 2
  const { cart, menuProduct, userIdentity } = await promiseHashAllSettled({
    cart: {
      defaultValue: getDefaultCart(),
      promise: getCartInfo(cookie, menu.cookie_prefix),
    },
    menuProduct: {
      defaultValue: {
        isAvailableInTheArea: undefined,
        isOutOfStock: undefined,
        product: null,
        storesCarryingProduct: [],
      },
      promise: fetchMenuProduct({
        productId,
        shoppingPreferences,
        stores: menu.stores,
      }),
    },
    userIdentity: {
      defaultValue: undefined,
      promise: getUserIdentity(request, menu.cookie_prefix),
    },
  });

  const { isAvailableInTheArea, isOutOfStock, product, storesCarryingProduct } =
    menuProduct;

  if (!product) {
    throw redirectToParentNotFound();
  }

  const headers = new Headers();

  const {
    dmContext: { jdid, sdk },
    headers: dmHeaders,
  } = await initializeDMContext(request);
  if (dmHeaders) {
    dmHeaders.forEach((value, key) => {
      headers.append(key, value);
    });
  }

  const productDetail = buildProductStoreViewModel({
    customLanguage: getCustomLanguage(pinnedStore),
    pinnedStore,
    product,
    storesCarryingProduct,
  });

  const featureFlags = await isFeatureFlagsEnabled({
    flagNames: [FLAGS.c3SpecialsRedesignMenu],
    request,
  });

  const applicableStoreSpecialIds = featureFlags[FLAGS.c3SpecialsRedesignMenu]
    ? getApplicableStoreSpecialIds(product)
    : new Set([]);

  // Resolve store specials early so we can filter by special_type
  const storeSpecials: StoreSpecials = await storeSpecialsPromise.catch(
    () => []
  );

  // Wave 2: Await independent requests + fetch queries that depend on Wave 1 results
  const {
    aeropayUser,
    brandSpecials,
    brandSpecialsQualifyingStatuses,
    goldConfig,
    productReviewsData,
    recommendedPDPRowViewModel,
    storeSpecialsQualifyingStatuses,
  } = await promiseHashAllSettled({
    aeropayUser: {
      defaultValue: undefined,
      promise: fetchAeropayUser({
        jwt: userIdentity?.jwtToken,
      }),
    },
    brandSpecials: {
      defaultValue: [],
      promise: brandSpecialsPromise,
    },
    brandSpecialsQualifyingStatuses: {
      defaultValue: {},
      promise: fetchBrandSpecialsQualifyingStatuses({
        brandSpecialIds: product.applicable_brand_special_ids,
        cartUuid: cart.uuid,
        storeId: pinnedStore.id,
      }),
    },
    goldConfig: {
      defaultValue: undefined,
      promise: goldConfigPromise,
    },
    productReviewsData: {
      defaultValue: [],
      promise: productReviewsPromise,
    },
    recommendedPDPRowViewModel: {
      defaultValue: {
        cart,
        customLanguage: getCustomLanguage(pinnedStore),
        row: undefined,
      },
      promise: buildRecommendedPDPRowViewModel({
        cart,
        isAvailableInTheArea,
        product,
        sdk,
        store: pinnedStore,
      }),
    },
    storeSpecialsQualifyingStatuses: {
      defaultValue: {},
      promise: fetchStoreSpecialsQualifyingStatuses({
        cartUuid: cart.uuid || '0',
        specialIds: applicableStoreSpecialIds,
        storeId: pinnedStore.id,
        storeSpecials,
      }),
    },
  });

  // Build product reviews view model from fetched data
  const productReviews = buildProductReviewsViewModel({
    aggregateRating: product.aggregate_rating,
    pinnedStore,
    productReviewsData,
    totalReviewCount: product.review_count ?? 0,
  });

  const shouldShowGold = getShouldShowGold({
    goldConfig,
    product: productDetail.product,
    store: pinnedStore,
    userSegments: userIdentity?.user_segments,
  });

  const isGoldUser = shouldShowGold && aeropayUser?.user_id;

  const loaderData: LoaderResponse = {
    applicableStoreSpecialIds,
    brandSpecials,
    brandSpecialsQualifyingStatuses: brandSpecialsQualifyingStatuses ?? {},
    isAvailableInTheArea,
    isGoldUser: !!isGoldUser,
    isOutOfStock,
    isSignedIn: !!userIdentity,
    jdid,
    pinnedStore: {
      currencyCode: pinnedStore.currency_code,
      hideDescription: pinnedStore.pdp_hide_description,
      id: pinnedStore.id,
      state: pinnedStore.state,
    },
    productDetail,
    productReviews,
    recommendedPDPRowViewModel,
    shouldShowGold,
    storeSpecials,
    storeSpecialsQualifyingStatuses: storeSpecialsQualifyingStatuses ?? {},
  };

  return data(loaderData, {
    headers,
  });
}

export const loader = (args: LoaderFunctionArgs) =>
  ddTrace.initOnce().trace(
    'loader.$basename.products.$productId.$slug',
    {
      measured: true,
    },
    async () => {
      return await _loader(args);
    }
  );

export default function ProductDetailRoute() {
  const {
    applicableStoreSpecialIds,
    brandSpecials,
    brandSpecialsQualifyingStatuses,
    isAvailableInTheArea,
    isGoldUser,
    isOutOfStock,
    isSignedIn,
    jdid,
    pinnedStore,
    productDetail,
    productReviews,
    recommendedPDPRowViewModel,
    shouldShowGold,
    storeSpecials,
    storeSpecialsQualifyingStatuses,
  } = useLoaderData<typeof loader>();
  const { product } = productDetail;

  const { hasPinnedStoreId, pinning } = useStoreSwitcher();
  const reviewSectionRef = useRef<HTMLDivElement>(null);

  const scrollToReviewSection = () => {
    reviewSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    registerJdid(jdid);
    registerJdidToRum(jdid);
  }, [jdid]);

  useEffect(() => {
    trackViewedProduct({
      isOutOfStock: !!isOutOfStock,
      product,
    });
  }, [isOutOfStock, product]);

  return (
    <main className="pdp-container-main">
      <ProductDetailCard
        applicableStoreSpecialIds={applicableStoreSpecialIds}
        brandSpecials={brandSpecials}
        brandSpecialsQualifyingStatuses={brandSpecialsQualifyingStatuses}
        isAvailableInTheArea={isAvailableInTheArea}
        isOutOfStock={isOutOfStock}
        isStoreChanging={pinning || !hasPinnedStoreId}
        pinnedStore={pinnedStore}
        productDetail={productDetail}
        productReviewsSummary={productReviews.summary}
        scrollToReviewSection={scrollToReviewSection}
        shouldShowGold={shouldShowGold}
        isGoldUser={isGoldUser}
        isSignedIn={isSignedIn}
        storeSpecials={storeSpecials}
        storeSpecialsQualifyingStatuses={storeSpecialsQualifyingStatuses}
      />

      <DMProductDetailsRow
        brandSpecials={brandSpecials}
        recommendedPDPRow={recommendedPDPRowViewModel.row}
        cart={recommendedPDPRowViewModel.cart}
        customLanguage={recommendedPDPRowViewModel.customLanguage}
        specials={storeSpecials}
        shouldShowGold={shouldShowGold}
      />

      <ProductReviewsSection
        ref={reviewSectionRef}
        productIdentifiers={{
          id: product.product_id,
          slug: product.unique_slug ?? '',
        }}
        productReviewsViewModel={productReviews}
      />
    </main>
  );
}
